import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, contentChildren, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';

import { TableCellFooterDirective } from './directives/table-cell-footer.directive';
import { TableCellHeaderDirective } from './directives/table-cell-header.directive';
import { TableCellDirective } from './directives/table-cell.directive';
import { TableRowRouterLinkFnResult } from './table.view-model';

@Component({
  selector: 'app-table',
  host: {
    class: 'flex flex-col',
    '[style.height.px]': 'virtualScroll() ? virtualScrollHeight() : null',
  },
  imports: [NgTemplateOutlet, NgClass, RouterLink, ScrollingModule],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T extends object> {
  public dataSource = input.required<readonly T[]>();
  public keyProperty = input.required<keyof T>();
  public rowClassFn = input<((row: T) => string) | undefined>();
  public rowRouterLinkFn = input<((row: T) => TableRowRouterLinkFnResult | undefined) | undefined>();
  public cellRowSpanFn = input<((row: T, cellKey: string) => number | undefined) | undefined>();
  public stickyHeaders = input(false);
  public virtualScroll = input(false);
  public virtualScrollItemSize = input(49);
  public virtualScrollHeight = input<number | undefined>();
  public readonly rowHover = output<T>();
  public readonly rowUnhover = output<void>();

  public cellDirectives = contentChildren(TableCellDirective<T>);
  public headerDirectives = contentChildren(TableCellHeaderDirective);
  public footerDirectives = contentChildren(TableCellFooterDirective);

  public footerDirectivesByKey = computed(() => {
    const directivesByKey = new Map<string, TableCellFooterDirective>();
    this.footerDirectives().forEach((directive) => {
      directivesByKey.set(directive.appTableCellFooterKey(), directive);
    });

    return directivesByKey;
  });

  public readonly trackByKey = (_: number, row: T): T[keyof T] => row[this.keyProperty()];
}
